import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import React from 'react';

import type { ChipSiblingData, SiblingCase, SiblingCaseProvider } from '../../interface/chip';
import { useCodingScreen } from '../../interface/context';
import type { EncounterData } from '../../interface/encounter';
import type { ProviderDirectoryEntry } from '../../interface/providerDirectory';
import type { SlotProps } from '../../interface/slotProps';
import {
  CHIP_COLORS,
  ENCOUNTER_METRIC_COLORS,
  INK,
  INK_MUTED,
  MONO_STACK,
  POPOVER_BORDER,
  POPOVER_RADIUS,
  POPOVER_SHADOW,
  SIBLING_ROW_COLORS,
  SIBLING_STATUS_COLORS,
  SURFACE_WARM
} from '../../styles/tokens';
import { Chip } from '../Chip';
import { ProviderDrawer } from '../ProviderDrawer';

const INTER_STACK = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const isNonEmptyString = (v: unknown): v is string =>
  typeof v === 'string' && v.trim() !== '';

const na = (v: unknown): string =>
  isNonEmptyString(v) || typeof v === 'number' ? String(v) : 'N/A';

const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
] as const;

/**
 * Reformats an `M/D/YYYY` encounter date (the adapter's `cmxDateTime.FORMATS.DATE_OF_SERVICE`
 * shape, e.g. `'3/12/2026'`) into the full `Month D, YYYY` display form, e.g. `'March 12, 2026'`.
 * Parses the string's own numeric parts rather than `new Date(...)` (no `date-fns`/`moment` --
 * neither is on the `project_starburst` isolation allowlist). A value that isn't `M/D/YYYY`-shaped
 * (including the `na()` fallback `'N/A'`) is returned verbatim rather than dropped.
 */
const formatDate = (value: string): string => {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
  if (!match) return value;
  const [, month, day, year] = match;
  const monthIndex = Number(month) - 1;
  const dayNumber = Number(day);
  if (monthIndex < 0 || monthIndex > 11 || dayNumber < 1 || dayNumber > 31) {
    return value;
  }
  return `${MONTHS_FULL[monthIndex]} ${dayNumber}, ${year}`;
};

const dayLabel = (dayOfStay: unknown, totalDays: unknown): string =>
  typeof dayOfStay === 'number'
    ? `Day ${dayOfStay} of ${typeof totalDays === 'number' ? totalDays : '?'}`
    : 'N/A';

const SIBLING_STATES = new Set(['done', 'auto', 'current', 'queued']);

const readSiblingCaseProvider = (raw: unknown): SiblingCaseProvider | undefined => {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  if (!isNonEmptyString(r.name)) return undefined;
  return {
    name: r.name,
    credential: isNonEmptyString(r.credential) ? r.credential : undefined,
    number: isNonEmptyString(r.number) ? r.number : undefined
  };
};

const readSiblingCase = (raw: unknown): SiblingCase | undefined => {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;

  const state = isNonEmptyString(r.state) && SIBLING_STATES.has(r.state) ? r.state : 'queued';

  const finalSigningProvider = readSiblingCaseProvider(r.finalSigningProvider);
  const authoringProvider = readSiblingCaseProvider(r.authoringProvider);

  const emCode =
    r.emCode != null && typeof r.emCode === 'object'
      ? (() => {
          const e = r.emCode as Record<string, unknown>;
          return isNonEmptyString(e.code) && isNonEmptyString(e.description)
            ? { code: e.code, description: e.description }
            : undefined;
        })()
      : undefined;

  const diagnoses = Array.isArray(r.diagnoses)
    ? r.diagnoses.filter(
        (d): d is { code: string; description: string } =>
          d != null &&
          typeof d === 'object' &&
          isNonEmptyString((d as Record<string, unknown>).code) &&
          isNonEmptyString((d as Record<string, unknown>).description)
      )
    : undefined;

  const sharedDiagnosisCodes = Array.isArray(r.sharedDiagnosisCodes)
    ? r.sharedDiagnosisCodes.filter(isNonEmptyString)
    : undefined;

  return {
    siblingCaseTitle: na(r.siblingCaseTitle),
    role: isNonEmptyString(r.role) ? r.role : undefined,
    state: state as SiblingCase['state'],
    statusLabel: na(r.statusLabel),
    finalSigningProvider,
    authoringProvider,
    emCode,
    diagnoses: diagnoses && diagnoses.length > 0 ? diagnoses : undefined,
    sharedDiagnosisCodes:
      sharedDiagnosisCodes && sharedDiagnosisCodes.length > 0 ? sharedDiagnosisCodes : undefined
  };
};

const readSiblingCases = (raw: unknown): SiblingCase[] =>
  Array.isArray(raw) ? raw.map(readSiblingCase).filter((c): c is SiblingCase => c !== undefined) : [];

const readSiblings = (siblings: unknown): ChipSiblingData | undefined => {
  if (siblings == null || typeof siblings !== 'object' || Array.isArray(siblings)) {
    return undefined;
  }
  const count = (siblings as ChipSiblingData).count;
  if (typeof count !== 'number' || count <= 0) {
    return undefined;
  }
  return {
    count,
    sharedBillingCodes: (siblings as ChipSiblingData).sharedBillingCodes === true,
    cases: readSiblingCases((siblings as ChipSiblingData).cases)
  };
};

const MetricBadge: React.FC<{ readonly value: string; readonly testId?: string }> = ({
  value,
  testId
}) => (
  <Text
    as="span"
    data-testid={testId}
    fontSize="11px"
    fontWeight={700}
    color={ENCOUNTER_METRIC_COLORS.text}
    bg={ENCOUNTER_METRIC_COLORS.bg}
    borderRadius="4px"
    px="6px"
    py="1px"
    lineHeight="1.4"
  >
    {value}
  </Text>
);

const DetailRow: React.FC<{
  readonly testId: string;
  readonly label: string;
  readonly value: string;
  readonly badge?: boolean;
}> = ({ testId, label, value, badge = false }) => (
  <Flex data-testid={testId} justify="space-between" align="center" gap="12px">
    <Text as="span" fontSize="11px" color={INK_MUTED}>
      {label}
    </Text>
    {badge ? (
      <MetricBadge value={value} />
    ) : (
      <Text as="span" fontSize="11.5px" fontWeight={600} color={INK}>
        {value}
      </Text>
    )}
  </Flex>
);

const providerLabel = (p: { readonly name: string; readonly credential?: string }): string =>
  p.credential ? `${p.name}, ${p.credential}` : p.name;

const DiagnosisChip: React.FC<{
  readonly code: string;
  readonly description: string;
  readonly shared: boolean;
}> = ({ code, description, shared }) => (
  <Text
    as="span"
    title={shared ? `${description} · shared with your case` : description}
    display="inline-flex"
    alignItems="center"
    gap="3px"
    fontFamily={MONO_STACK}
    fontSize="10.5px"
    fontWeight={700}
    color={shared ? CHIP_COLORS.warn : SIBLING_ROW_COLORS.roleText}
    bg={shared ? CHIP_COLORS.warnFaint : SIBLING_ROW_COLORS.panel2}
    borderWidth="1px"
    borderStyle="solid"
    borderColor={
      shared ? 'color-mix(in oklab, var(--warn, #f5a73f) 35%, transparent)' : POPOVER_BORDER
    }
    borderRadius="3px"
    px="5px"
    py="1px"
  >
    {shared ? (
      <Text as="span" aria-hidden fontSize="9px" lineHeight="1">
        ⇄
      </Text>
    ) : null}
    {code}
  </Text>
);

/** One "FINAL SIGNING PROVIDER"/"AUTHORING PROVIDER" row inside a `SiblingCaseRow` -- mirrors
 *  `case-card.tsx`'s two fixed `.provider-detail` blocks: an uppercase role eyebrow above the name.
 *  A present provider's name is a link-styled button (parallels case-card's `cmx-link` `onClick`
 *  spans) opening the provider-details modal; an absent provider renders plain "N/A" text, not a
 *  button -- there is nothing to show a modal for. */
const SiblingProviderRow: React.FC<{
  readonly testId: string;
  readonly label: string;
  readonly provider: SiblingCaseProvider | undefined;
  readonly onClick: (provider: SiblingCaseProvider) => void;
}> = ({ testId, label, provider, onClick }) => (
  <Flex direction="column" data-testid={testId}>
    <Text
      as="span"
      fontSize="8.5px"
      fontWeight={700}
      textTransform="uppercase"
      letterSpacing="0.06em"
      color={INK_MUTED}
    >
      {label}
    </Text>
    {provider ? (
      <Text
        as="button"
        type="button"
        data-testid={`${testId}-name`}
        fontFamily="inherit"
        fontSize="10px"
        fontWeight={600}
        color={CHIP_COLORS.primary}
        textDecoration="underline"
        bg="none"
        borderWidth="0"
        p="0"
        textAlign="left"
        cursor="pointer"
        _hover={{ opacity: 0.75 }}
        onClick={() => onClick(provider)}
      >
        {providerLabel(provider)}
      </Text>
    ) : (
      <Text as="span" data-testid={`${testId}-name`} fontSize="11px" color={SIBLING_ROW_COLORS.detailText}>
        N/A
      </Text>
    )}
  </Flex>
);

const SiblingCaseRow: React.FC<{
  readonly sibling: SiblingCase;
  readonly index: number;
  readonly onProviderClick: (role: string, provider: SiblingCaseProvider) => void;
}> = ({ sibling, index, onProviderClick }) => {
  const statusColors = SIBLING_STATUS_COLORS[sibling.state];
  const diagnoses = sibling.diagnoses ?? [];
  const sharedCodes = new Set(sibling.sharedDiagnosisCodes ?? []);

  return (
    <Flex
      as="li"
      data-testid="encounter-sibling-row"
      direction="column"
      gap="4px"
      borderWidth="1px"
      borderStyle="solid"
      borderColor={POPOVER_BORDER}
      borderRadius="8px"
      px="10px"
      py="8px"
      bg={SIBLING_ROW_COLORS.surface}
    >
      <Flex align="center" gap="7px">
        <Text as="span" fontSize="12px" fontWeight={600} color={INK}>
          {sibling.siblingCaseTitle}
        </Text>
        {sibling.role ? (
          <Text
            as="span"
            data-testid="encounter-sibling-role"
            fontSize="8.5px"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing="0.06em"
            color={SIBLING_ROW_COLORS.roleText}
            bg={SIBLING_ROW_COLORS.panel2}
            borderRadius="3px"
            px="5px"
            py="2px"
            whiteSpace="nowrap"
          >
            {sibling.role}
          </Text>
        ) : null}
        <Text
          as="span"
          data-testid="encounter-sibling-status"
          fontSize="8.5px"
          fontWeight={700}
          textTransform="uppercase"
          letterSpacing="0.06em"
          color={statusColors.text}
          bg={statusColors.bg}
          borderRadius="3px"
          px="5px"
          py="2px"
          ml="auto"
          whiteSpace="nowrap"
        >
          {sibling.statusLabel}
        </Text>
      </Flex>
      <Flex direction="column" gap="6px" mt="2px">
        <SiblingProviderRow
          testId="encounter-sibling-final-signing-provider"
          label="FINAL SIGNING PROVIDER"
          provider={sibling.finalSigningProvider}
          onClick={(provider) => onProviderClick('Final Signing Provider', provider)}
        />
        <SiblingProviderRow
          testId="encounter-sibling-authoring-provider"
          label="AUTHORING PROVIDER"
          provider={sibling.authoringProvider}
          onClick={(provider) => onProviderClick('Authoring Provider', provider)}
        />
      </Flex>
      {sibling.emCode ? (
        <Flex data-testid="encounter-sibling-em-code" align="baseline" gap="7px">
          <Text
            as="span"
            fontFamily={MONO_STACK}
            fontSize="11px"
            fontWeight={700}
            color={INK}
            flexShrink={0}
          >
            {sibling.emCode.code}
          </Text>
          <Text as="span" fontSize="11px" color={SIBLING_ROW_COLORS.detailText} lineHeight="1.4">
            {sibling.emCode.description}
          </Text>
        </Flex>
      ) : null}
      {diagnoses.length > 0 ? (
        <Flex wrap="wrap" gap="5px" mt="2px">
          {diagnoses.map((d) => (
            <DiagnosisChip
              key={d.code}
              code={d.code}
              description={d.description}
              shared={sharedCodes.has(d.code)}
            />
          ))}
        </Flex>
      ) : null}
      <Text
        as="button"
        type="button"
        data-testid="encounter-sibling-merge"
        alignSelf="flex-start"
        display="inline-flex"
        alignItems="center"
        gap="4px"
        mt="5px"
        fontFamily="inherit"
        fontSize="10.5px"
        fontWeight={600}
        color={CHIP_COLORS.primary}
        bg={CHIP_COLORS.primaryFaint}
        borderWidth="1px"
        borderStyle="solid"
        borderColor={CHIP_COLORS.primarySoft}
        borderRadius="4px"
        px="8px"
        py="3px"
        cursor="pointer"
        whiteSpace="nowrap"
        _hover={{ bg: CHIP_COLORS.primary, color: CHIP_COLORS.surface, borderColor: CHIP_COLORS.primary }}
        aria-label={`Merge case ${index + 1} into this case`}
      >
        ⇢ Merge into this case
      </Text>
    </Flex>
  );
};

/** How many related-case rows render inline in the popover before "View all" takes over. */
const INLINE_SIBLING_LIMIT = 3;

const SiblingCasesSection: React.FC<{
  readonly siblings: readonly SiblingCase[];
  readonly onViewAll: () => void;
  readonly onProviderClick: (role: string, provider: SiblingCaseProvider) => void;
}> = ({ siblings, onViewAll, onProviderClick }) => {
  const anyShared = siblings?.some((s) => (s.sharedDiagnosisCodes ?? []).length > 0);
  const visibleSiblings = siblings?.slice(0, INLINE_SIBLING_LIMIT) ?? [];
  const hiddenCount = (siblings?.length ?? 0) - visibleSiblings.length;

  return (
    <Box data-testid="encounter-siblings-section">
      <Box
        as="span"
        aria-hidden
        display="block"
        h="1px"
        my="8px"
        bg={POPOVER_BORDER}
      />
      <Text
        data-testid="encounter-siblings-title"
        fontSize="9.5px"
        fontWeight={700}
        textTransform="uppercase"
        letterSpacing="0.07em"
        color={INK_MUTED}
        whiteSpace="nowrap"
      >
        Related cases
      </Text>
      <Flex as="ul" direction="column" gap="7px" mt="9px" pl="0" listStyleType="none">
        {visibleSiblings.map((sibling, index) => (
          <SiblingCaseRow
            key={index}
            sibling={sibling}
            index={index}
            onProviderClick={onProviderClick}
          />
        ))}
      </Flex>
      {hiddenCount > 0 ? (
        <Text
          as="button"
          type="button"
          data-testid="encounter-siblings-view-all"
          display="block"
          w="100%"
          mt="7px"
          fontFamily="inherit"
          fontSize="10.5px"
          fontWeight={600}
          color={CHIP_COLORS.primary}
          bg="none"
          borderWidth="0"
          textAlign="left"
          cursor="pointer"
          whiteSpace="nowrap"
          _hover={{ textDecoration: 'underline' }}
          onClick={onViewAll}
        >
          View all related {siblings.length} cases
        </Text>
      ) : null}
      {anyShared ? (
        <Flex
          data-testid="encounter-siblings-legend"
          align="center"
          gap="4px"
          mt="9px"
          fontSize="10px"
          color={CHIP_COLORS.warn}
          whiteSpace="nowrap"
        >
          <Text as="span" aria-hidden fontSize="9px" lineHeight="1">
            ⇄
          </Text>
          shared with your case
        </Flex>
      ) : null}
    </Box>
  );
};

export const Encounter: React.FC<SlotProps> = ({ value }) => {
  const data: EncounterData =
    value != null && typeof value === 'object' && !Array.isArray(value)
      ? (value as EncounterData)
      : {};

  const dos = formatDate(na(data.dateOfService));
  const day = dayLabel(data.dayOfStay, data.totalDays);
  const siblings = readSiblings(data.siblings);
  const hasSiblings = siblings !== undefined;
  const accent = hasSiblings ? CHIP_COLORS.warn : CHIP_COLORS.primary;
  // The "View all" dialog's open state is owned at THIS level, outside the hover Popover, and the
  // Modal itself renders as a sibling of <Popover> (not inside PopoverContent): opening it moves the
  // pointer onto the dialog's own overlay, away from the hover trigger, so the popover closes the
  // instant the dialog appears -- the dialog must not be a descendant of what's closing.
  const { isOpen: isAllOpen, onOpen: onOpenAll, onClose: onCloseAll } = useDisclosure();
  // Without an explicit `finalFocusRef`, closing the dialog leaves Chakra's focus-return to fall
  // back past the (by-then-unmounted) "View all" button to the first focusable/tabbable element on
  // the page -- which, in this header bar, is the PatientSummaryBar's leftmost chip (PatientInfo).
  // Since PatientInfo's own Popover ALSO opens on focus (trigger="hover" wires focus/blur too, for
  // keyboard users), that stray refocus was spuriously popping its popover open. Anchoring focus
  // return to this chip's own trigger keeps it from drifting to an unrelated sibling molecule.
  const encounterTriggerRef = React.useRef<HTMLDivElement>(null);

  const [selectedProvider, setSelectedProvider] = React.useState<{
    readonly role: string;
    readonly provider: SiblingCaseProvider;
  } | null>(null);
  const {
    isOpen: isProviderOpen,
    onOpen: onOpenProvider,
    onClose: onCloseProvider
  } = useDisclosure();
  const { api } = useCodingScreen();
  // 'idle' never actually shows in the drawer (handleProviderClick always moves straight to
  // 'loading' or 'success' in the same tick it opens) -- kept as the pre-first-click resting value
  // so the type doesn't need a bogus initial fetch result.
  const [providerFetch, setProviderFetch] = React.useState<'idle' | 'loading' | 'success' | 'error'>(
    'idle'
  );
  const [providerDetails, setProviderDetails] = React.useState<ProviderDirectoryEntry | undefined>(
    undefined
  );
  // Guards against a stale, slower response overwriting a newer selection's result if the user
  // clicks a second provider before the first fetch resolves -- only the LATEST click's response is
  // ever applied.
  const providerFetchIdRef = React.useRef(0);

  const handleOpenAll = (): void => {
    onCloseProvider();
    onOpenAll();
  };

  const handleProviderClick = (role: string, provider: SiblingCaseProvider): void => {
    onCloseAll();
    setSelectedProvider({ role, provider });
    setProviderDetails(undefined);
    onOpenProvider();
    const fetchId = ++providerFetchIdRef.current;

    if (!isNonEmptyString(provider.number) || !api?.fetchProviderDetails) {
      setProviderFetch('success');
      return;
    }
    setProviderFetch('loading');
    api
      .fetchProviderDetails({
        providerNumber: provider.number,
        providerName: provider.name,
        tenantCode: data.facility
      })
      .then((result) => {
        if (providerFetchIdRef.current !== fetchId) return; // a newer click already superseded this
        setProviderDetails(result);
        setProviderFetch('success');
      })
      .catch(() => {
        if (providerFetchIdRef.current !== fetchId) return;
        setProviderFetch('error');
      });
  };

  const agingValue = na(data.agingDays);
  const aging = agingValue === 'N/A' ? 'N/A' : `${agingValue}d`;
  const codableValue = na(data.codableDays);
  const codable = codableValue === 'N/A' ? 'N/A' : `${codableValue}d`;

  let ariaLabel = `Encounter: ${dos}, ${day}`;
  if (hasSiblings) {
    ariaLabel += `, ${siblings.count} related`;
    if (siblings.sharedBillingCodes) {
      ariaLabel += ', shared billing codes';
    }
  }
  ariaLabel += `, aging ${aging}`;

  return (
    <>
    <Popover trigger="hover" placement="bottom-start">
      <PopoverTrigger>
        <Chip
          ref={encounterTriggerRef}
          testId="encounter"
          tone={hasSiblings ? 'warn' : 'primary'}
          ariaLabel={ariaLabel}
        >
          <Box
            as="span"
            aria-hidden
            data-testid="encounter-icon"
            w="16px"
            h="16px"
            flexShrink={0}
            borderRadius="50%"
            bg={CHIP_COLORS.primary}
            color={CHIP_COLORS.surface}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            fontSize="9px"
            lineHeight="1"
          >
            🏥
          </Box>
          <Text as="span" fontWeight={700} color={INK}>
            {dos}
          </Text>
          <Text as="span" aria-hidden color={INK_MUTED}>
            ·
          </Text>
          <Text as="span" fontWeight={700} color={CHIP_COLORS.primary}>
            {day}
          </Text>
          {hasSiblings ? (
            <Flex
              as="span"
              align="center"
              gap="4px"
              data-testid="encounter-sib"
              pl="6px"
              borderLeftWidth="1px"
              borderLeftStyle="solid"
              borderLeftColor={CHIP_COLORS.warn}
            >
              <Text as="span" fontWeight={700} color={CHIP_COLORS.warn} whiteSpace="nowrap">
                ⚠ {siblings.count} related
              </Text>
              {siblings.sharedBillingCodes ? (
                <Box
                  as="span"
                  aria-hidden
                  data-testid="encounter-sib-dot"
                  w="6px"
                  h="6px"
                  flexShrink={0}
                  borderRadius="50%"
                  bg={CHIP_COLORS.warn}
                  boxShadow={CHIP_COLORS.warnGlow}
                />
              ) : null}
            </Flex>
          ) : null}
          <Text as="span" aria-hidden data-testid="encounter-divider" color={INK_MUTED}>
            ·
          </Text>
          {aging === 'N/A' ? (
            <Text as="span">{aging}</Text>
          ) : (
            <MetricBadge value={aging} testId="encounter-aging-badge" />
          )}
          <Box
            as="span"
            aria-hidden
            data-testid="encounter-open"
            color={INK_MUTED}
            lineHeight="1"
            _groupHover={{ color: accent }}
          >
            ⤢
          </Box>
        </Chip>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          data-testid="encounter-popover"
          bg={SURFACE_WARM}
          border={`1px solid ${POPOVER_BORDER}`}
          borderRadius={POPOVER_RADIUS}
          boxShadow={POPOVER_SHADOW}
          padding="11px 13px 12px"
          width="312px"
          fontFamily={INTER_STACK}
        >
          <Flex direction="column" gap="6px">
            <DetailRow
              testId="encounter-admission"
              label="Admit"
              value={formatDate(na(data.admissionDate))}
            />
            <DetailRow
              testId="encounter-discharge"
              label="Discharge"
              value={formatDate(na(data.dischargeDate))}
            />
          </Flex>
          <Box
            as="span"
            aria-hidden
            data-testid="encounter-popover-divider"
            display="block"
            h="1px"
            my="8px"
            bg={POPOVER_BORDER}
          />
          <Flex direction="column" gap="6px">
            <DetailRow
              testId="encounter-aging"
              label="Aging"
              value={aging}
              badge={aging !== 'N/A'}
            />
            <DetailRow
              testId="encounter-codable"
              label="Codeable"
              value={codable}
              badge={codable !== 'N/A'}
            />
          </Flex>
          {hasSiblings && siblings.cases && siblings.cases.length > 0 ? (
            <SiblingCasesSection
              siblings={siblings.cases}
              onViewAll={handleOpenAll}
              onProviderClick={handleProviderClick}
            />
          ) : null}
        </PopoverContent>
      </Portal>
    </Popover>
    {hasSiblings && siblings.cases && siblings.cases.length > 0 ? (
      <Modal
        isOpen={isAllOpen}
        onClose={onCloseAll}
        size="lg"
        scrollBehavior="outside"
        finalFocusRef={encounterTriggerRef}
      >
        <ModalOverlay />
        <ModalContent
          data-testid="encounter-siblings-dialog"
          bg={SURFACE_WARM}
          border={`1px solid ${POPOVER_BORDER}`}
          borderRadius={POPOVER_RADIUS}
        >
          <ModalHeader
            data-testid="encounter-siblings-dialog-title"
            fontSize="14px"
            fontWeight={700}
            color={INK}
            borderBottomWidth="1px"
            borderColor={POPOVER_BORDER}
          >
            Related cases ({siblings.count})
          </ModalHeader>
          <ModalCloseButton aria-label="Close" />
          <ModalBody py="14px">
            <Flex as="ul" direction="column" gap="7px" pl="0" listStyleType="none">
              {siblings.cases.map((sibling, index) => (
                <SiblingCaseRow
                  key={index}
                  sibling={sibling}
                  index={index}
                  onProviderClick={handleProviderClick}
                />
              ))}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    ) : null}
    {selectedProvider ? (
      <ProviderDrawer
        isOpen={isProviderOpen}
        onClose={onCloseProvider}
        finalFocusRef={encounterTriggerRef}
        selectedProvider={selectedProvider}
        facility={data.facility}
        providerFetch={providerFetch}
        providerDetails={providerDetails}
      />
    ) : null}
    </>
  );
};
